import { type FC, lazy, Suspense, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { ROUTES } from '@consts/routes';
import ProfilePage from '@features/profile';
import TicketsPage from '@features/tickets/pages/tickets';
import { Loader } from '@ui/index';
import { menuConfig, type NavigationMenuItem } from '../../consts/navigation-menu';
import { NotFound404 } from '../pages/NotFound404';
import Objects from '../pages/Objects';
import * as Styled from './styled';

const ObjectsPage = lazy(() => import('@features/objects'));
const CallsPage = lazy(() => import('@features/calls'));
const ConnectionsPage = lazy(() => import('@features/connections'));
const NewsPage = lazy(() => import('@features/news'));
const EnterprisesNewsPage = lazy(() => import('@features/enterprises-news'));
const EquipmentPage = lazy(() => import('@features/equipment'));
const PeoplePage = lazy(() => import('@features/people'));
const UsersPage = lazy(() => import('@features/users'));
const RolesPage = lazy(() => import('@features/roles'));
const TenantsPage = lazy(() => import('@features/tenants'));
const RentObjectsPage = lazy(() => import('@features/rent-objects'));
const EnterprisesPage = lazy(() => import('@features/enterprises'));
const ParkingPage = lazy(() => import('@features/parking-slots'));
const ParkingLotsPage = lazy(() => import('@features/parking-lots'));
const CamerasPage = lazy(() => import('@pages/cctv'));
const GuestsPage = lazy(() => import('@features/guests'));
const MarketCompanies = lazy(() => import('@features/market-companies'));
const MarketOffersPage = lazy(() => import('@features/market-offers'));
const VotingPage = lazy(() => import('@features/voting'));
const QuarantinePage = lazy(() => import('@features/quarantine'));
// const StatisticPage = lazy(() => import('@features/statistics'));
const TicketsDisplayPage = lazy(() => import('@features/tickets-display'));
const ReportsPage = lazy(() => import('@features/reports'));
const ScheduleSettingsPage = lazy(() => import('@features/schedule'));
const AlarmsPage = lazy(() => import('@features/alarms'));
const ChatsPage = lazy(() => import('@features/socket-chats-reworked'));
const EnterprisesCompanyPage = lazy(() => import('@features/enterprise-company'));
const EnterprisesEmployeePage = lazy(() => import('@features/enterprises-employee'));
const EnterprisesPassPage = lazy(() => import('@features/enterprises-pass'));
const EnterprisesContactsPage = lazy(() => import('@features/enterprises-contacts'));
const EnterprisesObjectsPage = lazy(() => import('@features/enterprises-objects'));
const ApartmentAcceptance = lazy(
  () => import('@features/apartment-acceptance/pages/schedule-page')
);

const AcceptanceDashboard = lazy(
  () => import('@features/apartment-acceptance/pages/dashboard-page')
);

const EventsLogPage = lazy(() => import('@features/events-log'));

const EnterprisesParkingSlotsPage = lazy(
  () => import('@features/enterprises-parking-slots')
);

const StuffPage = lazy(() => import('@features/stuff'));
const BarriersPage = lazy(() => import('@features/barriers'));
const JournalPage = lazy(() => import('@features/journal-user-actions'));
const DashboardPage = lazy(() => import('@features/dashboard'));
const DashboardClimatePage = lazy(() => import('@features/dashboard-climate'));
const ContainersPage = lazy(() => import('@features/containers'));
const AccessKeysPage = lazy(() => import('@features/access-keys'));
const EnterpriseContractingPage = lazy(() => import('@features/enterprise-contractor'));
const SecurityPage = lazy(() => import('@features/security'));
const UserAppPage = lazy(() => import('@features/user-app'));
const DoorsPage = lazy(() => import('@features/doors'));

const StatisticCommunicationPage = lazy(
  () => import('@features/statistics-communication')
);

const TboSchedulePage = lazy(() => import('@features/tbo-schedule'));
const JobSchedulePage = lazy(() => import('@features/job-schedule'));
const EventServicePage = lazy(() => import('@features/event-service'));
const DevicesHintsPage = lazy(() => import('@features/devices-hints'));

const EnterprisesTicketsPage = lazy(
  () => import('@features/enterprises-tickets/pages/tickets')
);

const EnterprisesTicketPage = lazy(
  () => import('@features/enterprises-tickets/pages/ticket')
);

const ActivityPage = lazy(() => import('@features/activity'));
const ComparisonsPage = lazy(() => import('@features/counters-check/pages/comparisons'));

const ComparisonPage = lazy(
  () => import('@features/counters-check/pages/comparison-result')
);

const PlannedWorksPage = lazy(() => import('@features/planned-works'));
const PassConfigPage = lazy(() => import('@features/pass-config'));
const PlannedWorkSchedulePage = lazy(() => import('@features/planned-work-schedule'));
const DebtorsGeneralSettings = lazy(() => import('@features/debtors-settings'));
const DebtorsAccess = lazy(() => import('@features/debtors-access'));
const ConciergePage = lazy(() => import('@features/concierge'));
const EnterpriseVehiclesPage = lazy(() => import('@features/vehicles'));

/** Pages */
const RentPage = lazy(() => import('@pages/rent'));
const AccountsPage = lazy(() => import('@pages/accounts'));
const DebtorsPage = lazy(() => import('@pages/debtors-list'));
const AccessLevelPage = lazy(() => import('@pages/access-level'));
const UsersGroupsPage = lazy(() => import('@pages/users-groups'));
const SchedulePage = lazy(() => import('@pages/access-schedule'));
const TicketsTypesPage = lazy(() => import('@pages/tickets-types'));
const AccessGroupsPage = lazy(() => import('@pages/access-groups'));
const DebtorsReceipts = lazy(() => import('@pages/debtors-receipts'));
const DebtorsNotifications = lazy(() => import('@pages/debtors-notifications'));
const TypesGuests = lazy(() => import('@pages/types-guests'));
const CheckLists = lazy(() => import('@pages/check-lists'));
const CountersPage = lazy(() => import('@pages/counters'));
const CompanyPage = lazy(() => import('@pages/company'));
const InspectionsPage = lazy(() => import('@pages/inspections'));
const ClientPremisesPage = lazy(() => import('@pages/client-premises'));
const IndicatorsPanelPage = lazy(() => import('@pages/indicators-panel'));
const SmsGatewaySettingsPage = lazy(() => import('@pages/sms-gateway-settings'));
const EnterprisesNewPage = lazy(() => import('@pages/enterprises'));
const WorksPage = lazy(() => import('@pages/works'));
const WorkFlowsPage = lazy(() => import('@pages/work-flows'));
const Stories = lazy(() => import('@pages/stories'));
const AccountsNew = lazy(() => import('@pages/accounts-new'));
const QualificationsPage = lazy(() => import('@pages/qualifications'));
const EmployeeAssignment = lazy(() => import('@pages/employee-assignment'));
const ResidentsNewPage = lazy(() => import('@pages/residents-new'));
const PassesPage = lazy(() => import('@pages/passes'));
const PollsPage = lazy(() => import('@pages/polls'));
const BarriersNewPage = lazy(() => import('@pages/barriers-new'));
const TicketsFields = lazy(() => import('@pages/tickets-fields'));
const TicketsSchemas = lazy(() => import('@pages/tickets-schemas/pages/tickets-schemas'));
const TicketsSchema = lazy(() => import('@pages/tickets-schemas/pages/tickets-schema'));
const IntercomsPage = lazy(() => import('@pages/intercoms'));
const VehiclesPage = lazy(() => import('@pages/vehicles'));
const AnalyticsPage = lazy(() => import('@pages/analytics'));
const DocumentFlowPage = lazy(() => import('@pages/document-flow'));
const AppealsPage = lazy(() => import('@pages/appeals'));

export const Main: FC = () => {
  const [title, setTitle] = useState('');
  const location = useLocation();

  const menu = menuConfig.items.reduce(
    (acc: NavigationMenuItem[], { items, ...curr }) =>
      acc.concat(items ?? []).concat(curr),
    []
  );

  useEffect(() => {
    const pathname = location.pathname.split('/')[1];
    const { text } = menu.find((i) => pathname === i.link) || {};

    setTitle(text || '');
  }, [location]);

  return (
    <Styled.Main>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{title}</title>
      </Helmet>
      <Suspense fallback={<Loader isLoading />}>
        <Switch>
          {/* MANAGEMENT */}
          <Route exact path={`/${ROUTES.PEOPLE}`} component={PeoplePage} />
          <Route exact path={`/${ROUTES.PEOPLE}/:id`} component={PeoplePage} />
          <Route exact path={`/${ROUTES.USERS}`} component={UsersPage} />
          <Route exact path={`/${ROUTES.USERS}/:id`} component={UsersPage} />
          <Route exact path={`/${ROUTES.ROLES}`} component={RolesPage} />
          <Route exact path={`/${ROUTES.OBJECTS_NEW}`} component={ObjectsPage} />
          <Route exact path={`/${ROUTES.OBJECTS_NEW}/:other`} component={ObjectsPage} />
          <Route exact path={`/${ROUTES.OBJECTS}`} component={Objects} />
          <Route exact path={`/${ROUTES.TENANTS}`} component={TenantsPage} />
          <Route exact path={`/${ROUTES.RENT_OBJECTS}`} component={RentObjectsPage} />
          <Route exact path={`/${ROUTES.ENTERPRISES}`} component={EnterprisesPage} />
          <Route exact path={`/${ROUTES.PARKING}`} component={ParkingPage} />
          <Route exact path={`/${ROUTES.PARKING_LOTS}`} component={ParkingLotsPage} />
          <Route exact path={`/${ROUTES.CONTAINERS}`} component={ContainersPage} />
          <Route exact path={`/${ROUTES.INTERCOMS}`} component={IntercomsPage} />
          <Route exact path={`/${ROUTES.INTERCOMS}/:id`} component={IntercomsPage} />
          <Route exact path={`/${ROUTES.CCTV}`} component={CamerasPage} />
          <Route exact path={`/${ROUTES.CCTV}/:id`} component={CamerasPage} />
          <Route exact path={`/${ROUTES.DOORS}`} component={DoorsPage} />
          <Route exact path={`/${ROUTES.BARRIERS}`} component={BarriersPage} />
          <Route exact path={`/${ROUTES.GUESTS}`} component={GuestsPage} />
          <Route exact path={`/${ROUTES.GUESTS}/:id`} component={GuestsPage} />
          <Route exact path={`/${ROUTES.VEHICLES}`} component={VehiclesPage} />
          <Route exact path={`/${ROUTES.VEHICLES}/:id`} component={VehiclesPage} />
          <Route exact path={`/${ROUTES.MARKET_COMPANIES}`} component={MarketCompanies} />
          <Route
            exact
            path={`/${ROUTES.MARKET_COMPANIES}/:id`}
            component={MarketCompanies}
          />
          <Route exact path={`/${ROUTES.MARKET_OFFERS}`} component={MarketOffersPage} />
          <Route
            exact
            path={`/${ROUTES.MARKET_OFFERS}/:id`}
            component={MarketOffersPage}
          />
          <Route exact path={`/${ROUTES.COMPANY}`} component={CompanyPage} />
          <Route exact path={`/${ROUTES.NEWS}`} component={NewsPage} />
          <Route exact path={`/${ROUTES.VOTING}`} component={VotingPage} />
          <Route exact path={`/${ROUTES.VOTING}/:id`} component={VotingPage} />
          <Route exact path={`/${ROUTES.QUARANTINE}`} component={QuarantinePage} />
          <Route exact path={`/${ROUTES.ANALYTICS}`} component={AnalyticsPage} />
          <Route exact path={`/${ROUTES.DOCUMENT_FLOW}`} component={DocumentFlowPage} />
          {/* <Route exact path={`/${ROUTES.STATISTICS}`} component={StatisticPage} /> */}
          <Route exact path={`/${ROUTES.STATISTICS}`} component={IndicatorsPanelPage} />
          <Route exact path={`/${ROUTES.COUNTERS}`} component={CountersPage} />
          <Route
            exact
            path={`/${ROUTES.TICKETS_DISPLAY}`}
            component={TicketsDisplayPage}
          />
          <Route exact path={`/${ROUTES.TICKETS}`} component={TicketsPage} />
          <Route exact path={`/${ROUTES.TICKETS}/:id`} component={TicketsPage} />
          <Route path={`/${ROUTES.REPORTS}`} component={ReportsPage} />
          <Route exact path={`/${ROUTES.ALARMS}`} component={AlarmsPage} />
          <Route exact path={`/${ROUTES.APPOINTMENT}`} component={ScheduleSettingsPage} />
          <Route exact path={`/${ROUTES.CALLS}`} component={CallsPage} />
          <Route exact path={`/${ROUTES.CONNECTIONS}`} component={ConnectionsPage} />
          <Route exact path={`/${ROUTES.PASSES}`} component={PassesPage} />
          <Route exact path={`/${ROUTES.PASSES}/:id`} component={PassesPage} />
          <Route exact path={`/${ROUTES.PROFILE}`} component={ProfilePage} />
          <Route exact path={`/${ROUTES.ENTERPRISE_PROFILE}`} component={ProfilePage} />
          <Route exact path={`/${ROUTES.EVENTS_LOG}`} component={EventsLogPage} />
          <Route exact path={`/${ROUTES.EQUIPMENT}`} component={EquipmentPage} />
          <Route exact path={`/${ROUTES.ACCESS_KEYS}`} component={AccessKeysPage} />
          <Route exact path={`/${ROUTES.ACCESS_GROUPS}`} component={AccessGroupsPage} />
          <Route
            exact
            path={`/${ROUTES.ACCESS_GROUPS}/:id`}
            component={AccessGroupsPage}
          />
          <Route exact path={`/${ROUTES.ACTIVITY}`} component={ActivityPage} />
          <Route
            exact
            path={`/${ROUTES.STATISTICS_COMMUNICATION}`}
            component={StatisticCommunicationPage}
          />
          {/* <Route exact path={`/${ROUTES.INDICATORS_PANEL}`} component={IndicatorsPanelPage} /> */}
          <Route exact path={`/${ROUTES.USERS_GROUPS}`} component={UsersGroupsPage} />
          <Route exact path={`/${ROUTES.USERS_GROUPS}/:id`} component={UsersGroupsPage} />
          <Route exact path={`/${ROUTES.ACCESS_LEVEL}`} component={AccessLevelPage} />
          <Route exact path={`/${ROUTES.ACCESS_LEVEL}/:id`} component={AccessLevelPage} />
          <Route exact path={`/${ROUTES.SCHEDULE}`} component={SchedulePage} />
          <Route exact path={`/${ROUTES.SCHEDULE}/:id`} component={SchedulePage} />
          <Route
            exact
            path={`/${ROUTES.ENTERPRISE_CONTRACTOR}`}
            component={EnterpriseContractingPage}
          />
          <Route
            exact
            path={`/${ROUTES.ENTERPRISE_CONTRACTOR}/:id`}
            component={EnterpriseContractingPage}
          />
          <Route
            path={`/${ROUTES.APARTMENT_ACCEPTANCE}/:id`}
            component={ApartmentAcceptance}
          />
          <Route
            exact
            path={`/${ROUTES.APARTMENT_ACCEPTANCE}`}
            component={AcceptanceDashboard}
          />
          <Route exact path={`/${ROUTES.JOURNAL_USER_ACTIONS}`} component={JournalPage} />
          <Route path={`/${ROUTES.MESSAGES}`} component={ChatsPage} />
          <Route path={`/${ROUTES.POLLS}`} component={PollsPage} />
          <Route path={`/${ROUTES.STUFF}`} component={StuffPage} />
          <Route path={`/${ROUTES.SECURITY}`} component={SecurityPage} />
          <Route path={`/${ROUTES.USER_APP}`} component={UserAppPage} />
          <Route path={`/${ROUTES.CLIENT_PREMISES}`} component={ClientPremisesPage} />
          <Route
            path={`/${ROUTES.SMS_GATEWAY_SETTINGS}`}
            component={SmsGatewaySettingsPage}
          />
          <Route path={`/${ROUTES.TBO_SCHEDULE}`} component={TboSchedulePage} />
          <Route path={`/${ROUTES.JOB_SCHEDULE}`} component={JobSchedulePage} />
          <Route exact path="/" render={() => <Redirect to={`/${ROUTES.PROFILE}`} />} />
          <Route path={`/${ROUTES.WORKS}`} component={WorksPage} />
          <Route path={`/${ROUTES.WORKS}/:id`} component={WorksPage} />
          <Route exact path={`/${ROUTES.DASHBOARD}`} component={DashboardPage} />
          <Route
            exact
            path={`/${ROUTES.DASHBOARD_CLIMATE}`}
            component={DashboardClimatePage}
          />
          <Route exact path={`/${ROUTES.EVENT_SERVICE}`} component={EventServicePage} />
          <Route exact path={`/${ROUTES.TICKETS_TYPES}`} component={TicketsTypesPage} />
          <Route
            exact
            path={`/${ROUTES.TICKETS_TYPES}/:id`}
            component={TicketsTypesPage}
          />
          <Route exact path={`/${ROUTES.DEVICES_HINTS}`} component={DevicesHintsPage} />
          <Route exact path={`/${ROUTES.COUNTERS_CHECK}`} component={ComparisonsPage} />
          <Route path={`/${ROUTES.COUNTERS_CHECK}/:id`} component={ComparisonPage} />
          <Route exact path={`/${ROUTES.PLANNED_WORKS}`} component={PlannedWorksPage} />
          <Route
            exact
            path={`/${ROUTES.PLANNED_WORKS}/:id`}
            component={PlannedWorksPage}
          />
          <Route exact path={`/${ROUTES.PASS_CONFIG}`} component={PassConfigPage} />
          <Route
            exact
            path={`/${ROUTES.QUALIFICATIONS}`}
            component={QualificationsPage}
          />
          <Route
            exact
            path={`/${ROUTES.QUALIFICATIONS}/:id`}
            component={QualificationsPage}
          />
          <Route
            exact
            path={`/${ROUTES.PLANNED_WORK_SCHEDULE}`}
            component={PlannedWorkSchedulePage}
          />
          <Route exact path={`/${ROUTES.WORK_FLOWS}`} component={WorkFlowsPage} />
          <Route exact path={`/${ROUTES.WORK_FLOWS}/:id`} component={WorkFlowsPage} />
          <Route exact path={`/${ROUTES.DEBTORS_LIST}`} component={DebtorsPage} />
          <Route exact path={`/${ROUTES.DEBTORS_LIST}/:id`} component={DebtorsPage} />
          <Route
            exact
            path={`/${ROUTES.DEBTORS_NOTIFICATIONS}`}
            component={DebtorsNotifications}
          />
          <Route exact path={`/${ROUTES.DEBTORS_RECEIPTS}`} component={DebtorsReceipts} />
          <Route
            exact
            path={`/${ROUTES.DEBTORS_SETTINGS}`}
            component={DebtorsGeneralSettings}
          />
          <Route exact path={`/${ROUTES.DEBTORS_ACCESS}`} component={DebtorsAccess} />
          <Route exact path={`/${ROUTES.ACCOUNTS}`} component={AccountsPage} />
          <Route exact path={`/${ROUTES.ACCOUNTS}/:id`} component={AccountsPage} />
          <Route exact path={`/${ROUTES.CONCIERGE}`} component={ConciergePage} />
          <Route exact path={`/${ROUTES.CHECK_LISTS}`} component={CheckLists} />
          <Route exact path={`/${ROUTES.CHECK_LISTS}/:id`} component={CheckLists} />
          <Route exact path={`/${ROUTES.INSPECTIONS}`} component={InspectionsPage} />
          <Route exact path={`/${ROUTES.INSPECTIONS}/:id`} component={InspectionsPage} />
          <Route exact path={`/${ROUTES.RENT}`} component={RentPage} />
          <Route exact path={`/${ROUTES.RENT}/:id`} component={RentPage} />
          <Route exact path={`/${ROUTES.GUESTS_TYPES}`} component={TypesGuests} />
          <Route exact path={`/${ROUTES.GUESTS_TYPES}/:id`} component={TypesGuests} />
          <Route
            exact
            path={`/${ROUTES.ENTERPRISE_LIST}`}
            component={EnterprisesNewPage}
          />
          <Route
            exact
            path={`/${ROUTES.ENTERPRISE_LIST}/:id`}
            component={EnterprisesNewPage}
          />
          <Route exact path={`/${ROUTES.STORIES}`} component={Stories} />
          <Route exact path={`/${ROUTES.STORIES}/:id`} component={Stories} />
          <Route exact path={`/${ROUTES.ACCOUNTS_NEW}`} component={AccountsNew} />
          <Route exact path={`/${ROUTES.ACCOUNTS_NEW}/:id`} component={AccountsNew} />
          <Route
            exact
            path={`/${ROUTES.EMPLOYEE_ASSIGNMENT}`}
            component={EmployeeAssignment}
          />
          <Route exact path={`/${ROUTES.RESIDENTS_NEW}`} component={ResidentsNewPage} />
          <Route
            exact
            path={`/${ROUTES.RESIDENTS_NEW}/:id`}
            component={ResidentsNewPage}
          />
          <Route
            exact
            path={`/${ROUTES.RESIDENTS_NEW}/:id/appeals`}
            component={ResidentsNewPage}
          />
          <Route exact path={`/${ROUTES.BARRIERS_NEW}`} component={BarriersNewPage} />
          <Route exact path={`/${ROUTES.TICKETS_FIELDS}`} component={TicketsFields} />
          <Route exact path={`/${ROUTES.TICKETS_FIELDS}/:id`} component={TicketsFields} />
          <Route exact path={`/${ROUTES.TICKETS_SCHEMAS}`} component={TicketsSchemas} />
          <Route
            exact
            path={`/${ROUTES.TICKETS_SCHEMAS}/:id`}
            component={TicketsSchema}
          />
          <Route
            exact
            path={`/${ROUTES.TICKETS_SCHEMAS}/creation`}
            component={TicketsSchema}
          />
          <Route exact path={`/${ROUTES.APPEALS}`} component={AppealsPage} />
          <Route exact path={`/${ROUTES.APPEALS}/:id`} component={AppealsPage} />
          {/* ENTERPRISES */}
          <Route
            exact
            path={`/${ROUTES.ENTERPRISES_NEWS}`}
            component={EnterprisesNewsPage}
          />
          <Route
            exact
            path={`/${ROUTES.ENTERPRISES_NEWS}/:id`}
            component={EnterprisesNewsPage}
          />
          <Route
            exact
            path={`/${ROUTES.ENTERPRISES_VEHICLES}`}
            component={EnterpriseVehiclesPage}
          />
          <Route
            path={`/${ROUTES.ENTERPRISES_TICKETS}/:id`}
            component={EnterprisesTicketPage}
          />
          <Route
            exact
            path={`/${ROUTES.ENTERPRISES_TICKETS}`}
            component={EnterprisesTicketsPage}
          />
          <Route exact path={`/${ROUTES.ENTERPRISES_EVENTS}`} component={EventsLogPage} />
          <Route
            exact
            path={`/${ROUTES.ENTERPRISES_COMPANY}`}
            component={EnterprisesCompanyPage}
          />
          <Route
            exact
            path={`/${ROUTES.ENTERPRISES_EMPLOYEE}`}
            component={EnterprisesEmployeePage}
          />
          <Route
            exact
            path={`/${ROUTES.ENTERPRISES_PASSES}`}
            component={EnterprisesPassPage}
          />
          <Route
            exact
            path={`/${ROUTES.ENTERPRISES_CONTACTS}`}
            component={EnterprisesContactsPage}
          />
          <Route
            exact
            path={`/${ROUTES.ENTERPRISES_OBJECTS}`}
            component={EnterprisesObjectsPage}
          />
          <Route
            exact
            path={`/${ROUTES.ENTERPRISES_PARKING}`}
            component={EnterprisesParkingSlotsPage}
          />
          <Route path={`/${ROUTES.ENTERPRISES_STUFF}`} component={StuffPage} />
          {/* Not found page */}
          <Route component={NotFound404} />
        </Switch>
      </Suspense>
    </Styled.Main>
  );
};
